// Dependency-free hydrology helpers: the drainage network classes, which
// loaded artifacts belong to the drainage network, and stream-highlight
// buffers. Kept free of relative imports so node --test can load it directly
// (see __tests__/hydrologyLegend.test.ts).
export const DRAINAGE_CLASSES = [
    { key: 'micro', label: 'Micro', shortLabel: 'Micro', threshold: 250, color: '#89CFF0', width: 1.0 },
    { key: 'minor', label: 'Minor', shortLabel: 'Minor', threshold: 500, color: '#82C4EA', width: 1.11 },
    { key: 'intermediate', label: 'Intermediate', shortLabel: 'Intermed', threshold: 2500, color: '#486DBB', width: 1.95 },
    { key: 'major', label: 'Major', shortLabel: 'Major', threshold: 5000, color: '#000080', width: 3.0 },
];
export const drainageClassForThreshold = (threshold) => {
    var _a;
    const n = typeof threshold === 'number' ? threshold : Number(threshold.trim());
    if (!Number.isFinite(n))
        return null;
    return (_a = DRAINAGE_CLASSES.find((c) => c.threshold === n)) !== null && _a !== void 0 ? _a : null;
};
// A drainage artifact reaches the viewer under its friendly name ("Micro") or,
// when no friendly name was computed, its file name ("streams_250",
// "streams_250_display.kml").
export const drainageClassForName = (name) => {
    var _a;
    const n = name.trim().toLowerCase();
    const threshold = n.match(/^streams?_(\d+)/);
    if (threshold)
        return drainageClassForThreshold(Number(threshold[1]));
    return (_a = DRAINAGE_CLASSES.find((c) => c.key === n)) !== null && _a !== void 0 ? _a : null;
};
// The class label for a stream threshold ("2500" -> "Intermediate"); an
// unrecognised threshold reads back as itself.
export const streamThresholdLabel = (threshold) => { var _a, _b; return (_b = (_a = drainageClassForThreshold(threshold)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : threshold; };
// Position of a stream layer in Micro-to-Major order; unknown names sort last.
export const drainageClassOrder = (name) => {
    const cls = drainageClassForName(name);
    return cls ? DRAINAGE_CLASSES.indexOf(cls) : DRAINAGE_CLASSES.length;
};
const candidateName = (artifact) => { var _a; return typeof ((_a = artifact.metadata) === null || _a === void 0 ? void 0 : _a.getName) === 'function' ? artifact.metadata.getName() : ''; };
const candidateMetadata = (artifact) => { var _a; return typeof ((_a = artifact.metadata) === null || _a === void 0 ? void 0 : _a.getMetadata) === 'function' ? artifact.metadata.getMetadata() : ''; };
// The server adds the artifact's storage object key to its metadata JSON; a
// pipeline drainage layer's key names its streams_<threshold> file.
const objectKeyFromMetadata = (metadataJson) => {
    if (!metadataJson)
        return '';
    try {
        const meta = JSON.parse(metadataJson);
        return typeof (meta === null || meta === void 0 ? void 0 : meta.object_key) === 'string' ? meta.object_key : '';
    }
    catch (_a) {
        return '';
    }
};
// A drainage-network layer is identified by its object key, or by a name that
// says so. A friendly class name alone ("Major") is not enough: a user's own
// layer can carry that name.
export const isStreamLayer = (name, metadataJson) => {
    const key = objectKeyFromMetadata(metadataJson).toLowerCase();
    if (key.includes('stream') || key.includes('drainage'))
        return true;
    const n = name.toLowerCase();
    return n.includes('stream') || n.includes('hydro') || n.includes('drainage');
};
export const isStreamArtifact = (artifact) => isStreamLayer(candidateName(artifact), candidateMetadata(artifact));
// The flow-accumulation threshold behind a stream artifact, from its object key
// (".../streams_2500_display.kml") or its class name; otherwise its name.
export const extractStreamThreshold = (artifact) => {
    const fromKey = objectKeyFromMetadata(candidateMetadata(artifact)).match(/streams?[_-]?(\d+)/i);
    if (fromKey)
        return fromKey[1];
    const name = candidateName(artifact);
    const cls = DRAINAGE_CLASSES.find((c) => c.key === name.trim().toLowerCase());
    if (cls)
        return String(cls.threshold);
    return name || 'Unknown';
};
// Each class has a data artifact and a _display twin under the same name, so
// classes are deduped; the result runs finest (Micro) to largest (Major).
export const visibleDrainageClasses = (names) => {
    const found = new Set();
    for (const name of names) {
        const cls = drainageClassForName(name);
        if (cls)
            found.add(cls.key);
    }
    return DRAINAGE_CLASSES.filter((c) => found.has(c.key));
};
// Drainage classes among the given (visible) artifacts, counting only real
// drainage-network artifacts so a user layer named "Major" adds nothing.
export const visibleDrainageClassesForArtifacts = (artifacts) => {
    const names = [];
    for (const artifact of artifacts) {
        if (isStreamArtifact(artifact))
            names.push(candidateName(artifact));
    }
    return visibleDrainageClasses(names);
};
const METERS_PER_FOOT = 0.3048;
export const DEFAULT_BUFFER_UNIT = 'ft';
export const formatBufferLabel = (distanceMeters, unit) => {
    if (distanceMeters === undefined || !Number.isFinite(distanceMeters) || distanceMeters <= 0)
        return undefined;
    if ((unit !== null && unit !== void 0 ? unit : DEFAULT_BUFFER_UNIT) === 'ft')
        return `${Math.round(distanceMeters / METERS_PER_FOOT)} ft buffer`;
    return `${Math.round(distanceMeters * 10) / 10} m buffer`;
};
// A saved stream highlight keeps its buffer in geometryJson.buffer_settings
// (written by useArtifactPromotion); absent when the highlight has no buffer.
export const parseStreamHighlightBuffer = (geometryJson) => {
    if (!geometryJson)
        return undefined;
    let g;
    try {
        g = JSON.parse(geometryJson);
    }
    catch (_a) {
        return undefined;
    }
    const settings = g === null || g === void 0 ? void 0 : g.buffer_settings;
    if (!settings)
        return undefined;
    const unit = settings.unit === 'ft' || settings.unit === 'm' ? settings.unit : undefined;
    return formatBufferLabel(Number(settings.distance_meters), unit);
};
